import React, { useCallback, useEffect, useState } from "react";
import { withStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";

import { Header } from "../../components";
import { BodyContainer } from "../../components/BaseComponents";
import { Dialog } from "../../components/BaseComponents/Dialog";
import { OrganizationsItem } from "./components/OrganizationsItem";
import OrganizationsStatistics from "./components/OrganizationsStats";
import { StyledOrgContainer } from "./styles";

import { getUsers } from "../../api/user";
import { getTokens } from "../../api/token";
import { createOrganization, getOrganizations } from "../../api/organization";

import { isAdmin } from "../../helpers/roles";

const styles = () => ({
  paper: {
    width: 1080,
    margin: "auto",
    overflow: "hidden",
    boxShadow: "0 1px 3px 0 #dce1e6",
    borderRadius: 5,
  },
  inputError: {
    "&:invalid": {
      border: "2px solid red",
      borderRadius: 5,
    },
  },
});

const Organizations = ({ classes, onDrawerToggle }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [organizations, setOrganizations] = useState([]);
  const [organizationsTotalStat, setOrganizationsTotalStat] = useState(null);
  const [statsMap, setStatsMap] = useState(new Map());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [errorText, setErrorText] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(true);

  const isUserAdmin = isAdmin();

  const ALPHABETIC_PATTERN = /[a-zA-Z]/;
  const ORGANIZATION_PATTERN = /^[a-zA-Z0-9_$#& ]*$/;

  const fetchOrganizations = useCallback(async () => {
    try {
      const orgs = await getOrganizations();
      setOrganizations(orgs);
      setOrganizationsTotalStat({ organizations: orgs });

      const statsPromises = orgs.map(async (org) => {
        const [users, tokens] = await Promise.all([
          getUsers({ organizationId: org.id }),
          getTokens({ organizationId: org.id }),
        ]);
        return { orgId: org.id, users, tokens };
      });

      const statsResults = await Promise.all(statsPromises);
      const newStatsMap = new Map(
        statsResults.map(({ orgId, users, tokens }) => [orgId, { users, tokens }])
      );
      setStatsMap(newStatsMap);
    } catch (error) {
      enqueueSnackbar("Failed to fetch organizations.", { variant: "error" });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleCreateOrganization = async () => {
    if (errorText) return;

    try {
      await createOrganization({ name: organizationName });
      enqueueSnackbar(`Successfully created organization ${organizationName}`, { variant: "success" });
      fetchOrganizations();
      setIsDialogOpen(false);
      setOrganizationName("");
    } catch {
      enqueueSnackbar("Unable to create organization.", { variant: "error" });
    }
  };

  const handleDialogCancel = () => {
    setOrganizationName("");
    setErrorText("");
    setIsDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setOrganizationName(value);

    if (value && !ALPHABETIC_PATTERN.test(value)) {
      setErrorText("The organization must contain letters");
    } else if (value && !ORGANIZATION_PATTERN.test(value)) {
      setErrorText("Invalid characters");
    } else {
      setErrorText("");
    }
  };

  const actionButtons = [
    {
      label: "Create Organization",
      action: () => setIsDialogOpen(true),
      enabled: isUserAdmin,
    },
  ];

  return (
    <>
      <Header
        headerTitle="Organizations"
        onDrawerToggle={onDrawerToggle}
        actionButtons={actionButtons}
      />
      <BodyContainer>
        <OrganizationsStatistics
          organizationsTotalStat={organizationsTotalStat}
          statsMap={[...statsMap]}
        />
        <Paper className={classes.paper}>
          <StyledOrgContainer>
            {organizations.map((org) => (
              <OrganizationsItem
                key={org.id}
                statsMap={statsMap}
                name={org.name}
                id={org.id}
              />
            ))}
          </StyledOrgContainer>
        </Paper>
      </BodyContainer>

      <Dialog
        onCancel={handleDialogCancel}
        isOpened={isDialogOpen}
        title="Create New Organization"
        buttonProps={{
          name: "Create",
          onAction: handleCreateOrganization,
          disabled: !organizationName,
        }}
      >
        <TextField
          variant="outlined"
          margin="dense"
          id="name"
          label="Org Name"
          type="text"
          fullWidth
          required
          InputProps={{
            inputProps: {
              className: `${isInputFocused ? "" : classes.inputError}`,
              maxLength: 20,
            },
          }}
          onFocus={() => setIsInputFocused(true)}
          onChange={handleInputChange}
          value={organizationName}
          error={Boolean(errorText)}
          helperText={errorText}
        />
      </Dialog>
    </>
  );
};

export default withStyles(styles)(Organizations);
